import Widget from "./Widget";
import { gql } from "@apollo/client";
import { Skeleton } from "@src/lib/ui/Skeleton";
import {
  AdminRecentlyEditedModulesQuery,
  useAdminRecentlyEditedModulesQuery,
} from "./RecentlyEditedModulesWidget.generated";
import ContentAvatar from "@src/features/contentLibrary/ContentAvatar";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import Link from "next/link";

type RecentlyEditedModule =
  AdminRecentlyEditedModulesQuery["AdminLibrary"]["objects"][number];

const RecentlyEditedModulesItems: React.FC<{
  modules: RecentlyEditedModule[];
}> = ({ modules }) => (
  <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto">
    {modules.map((obj) => (
      <Link
        href={{
          pathname: "/library/library-item/[id]",
          query: { id: obj.module?.libraryItem.id as string },
        }}
        key={obj.id}
        className="block w-44 shrink-0"
      >
        <Button
          variant="ghost"
          className="h-auto w-full items-stretch justify-start px-2 py-1 text-left"
        >
          <div className="flex w-full min-w-0 items-center justify-start gap-2">
            <ContentAvatar
              size={"24px"}
              contentType={"Module"}
              style={{ borderRadius: 8 }}
              defaultBackgroundHashKey={obj.module?.libraryItem.id as string}
              coverImage={obj.module?.libraryItem.coverImage}
              className="shrink-0"
            />
            <Text
              variant="compact-title"
              ellipsis
              ellipsisParentFlexDirection="row"
              ellipsisParentClassName="text-left"
              className="min-w-0 flex-1 text-left"
            >
              {obj.name.en || "Untitled Module"}
            </Text>
          </div>
        </Button>
      </Link>
    ))}
  </div>
);

export const useHasRecentlyEditedModules = (): boolean => {
  const { data, loading } = useAdminRecentlyEditedModulesQuery();
  return loading || (data?.AdminLibrary.objects.length ?? 0) > 0;
};

export const RecentlyEditedModulesSection: React.FC = () => {
  const { data, loading } = useAdminRecentlyEditedModulesQuery();

  if (!loading && data?.AdminLibrary.objects.length === 0) {
    return null;
  }

  return (
    <Widget
      title="Recently edited modules"
      className="min-w-0 basis-full gap-2"
    >
      {loading ? (
        <Skeleton className="h-9 w-full rounded-lg" />
      ) : (
        <RecentlyEditedModulesItems
          modules={data?.AdminLibrary.objects ?? []}
        />
      )}
    </Widget>
  );
};

const RecentlyEditedModulesWidget: React.FC = () => {
  const { data, loading } = useAdminRecentlyEditedModulesQuery();

  if (!loading && data?.AdminLibrary.objects.length === 0) {
    return null;
  }

  return (
    <Widget title="Recently Edited Modules">
      {loading ? (
        <Skeleton className="h-9 w-full rounded-lg" />
      ) : (
        <RecentlyEditedModulesItems
          modules={data?.AdminLibrary.objects ?? []}
        />
      )}
    </Widget>
  );
};

gql`
  query AdminRecentlyEditedModules {
    AdminLibrary(
      input: {
        filters: { itemType: { value: MODULE } }
        sort: { column: "lastEdited", descending: true, nullsLast: true }
      }
      pagination: { limit: 10 }
    ) {
      objects {
        id
        name {
          uuid
          en
        }
        lastEditedAt
        module {
          id
          libraryItem {
            id
            name {
              uuid
              en
            }
            coverImage {
              ...LibraryItemDetailPageCoverImage_CoverImage
            }
          }
        }
      }
    }
  }
`;

export default RecentlyEditedModulesWidget;
